import AddMoreWithReorder from '@modules/JobOffers/components/AddMoreWithReorder';
import LoadingModal from '@components/LoadingModal';
import { Box, Button, Card, CardContent, Chip, Grid, MenuItem, Slider, Stack, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import InputFieldControlled from '@components/InputFieldControlled';
import { createEmptyQuestion, QUESTIONNAIRE_STATUS_LABELS, QuestionnaireStatus } from './types';
import { useSaveQuestionnaire } from './hooks/useSaveQuestionnaire';
import QuestionEditor from './components/QuestionEditor';
import ProSelector from './components/ProSelector';

const StickyBar = styled(Box)(({ theme }) => ({
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 3),
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    marginLeft: -24,
    marginRight: -24,
    marginBottom: -24,
    width: 'calc(100% + 48px)',
}));

const difficultyMarks = [
    { value: 1, label: '1' },
    { value: 3, label: '3' },
    { value: 5, label: '5' },
    { value: 7, label: '7' },
    { value: 10, label: '10' },
];

const statusColor: Record<QuestionnaireStatus, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
    [QuestionnaireStatus.DRAFT]: 'warning',
    [QuestionnaireStatus.ACTIVE]: 'success',
    [QuestionnaireStatus.ARCHIVED]: 'default',
};

const QuestionnaireFormContent = ({ lockPro = false }: { lockPro?: boolean }) => {
    const {
        control,
        watch,
        setValue,
        formState: { isDirty },
    } = useFormContext();

    const status: QuestionnaireStatus = watch('status');

    // Prevent accidental navigation when dirty
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    const addQuestionDefault = createEmptyQuestion(0);

    return (
        <Grid container spacing={3}>
            {/* Left column - main fields */}
            <Grid item xs={12} md={8}>
                <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" mb={3}>
                            Informations générales
                        </Typography>

                        <InputFieldControlled
                            control={control}
                            name="title"
                            label="Titre du questionnaire"
                            required
                            containerStyle={{ mb: 2 }}
                            placeholder="Ex: Évaluation technique frontend"
                        />

                        <InputFieldControlled
                            control={control}
                            name="description"
                            label="Description"
                            multiline
                            numberOfLines={3}
                            optional
                            containerStyle={{ mb: 2 }}
                            placeholder="Décrivez l'objectif de ce questionnaire..."
                        />

                        <Box mb={3}>
                            <ProSelector
                                value={watch('proId') ?? null}
                                disabled={lockPro}
                                onChange={(newProId) => setValue('proId', newProId, { shouldDirty: true })}
                            />
                            {lockPro && (
                                <Typography variant="caption" color="text.secondary">
                                    L'Établissement est déterminé par la page courante.
                                </Typography>
                            )}
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                            {/* Difficulty 1-10 slider */}
                            <Box flex={1}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Niveau de difficulté
                                </Typography>
                                <Slider
                                    value={watch('difficultyLevel') ?? 5}
                                    min={1}
                                    max={10}
                                    step={1}
                                    marks={difficultyMarks}
                                    valueLabelDisplay="auto"
                                    onChange={(_, val) =>
                                        setValue('difficultyLevel', val as number, { shouldDirty: true })
                                    }
                                />
                            </Box>

                            <Box minWidth={140}>
                                <InputFieldControlled
                                    control={control}
                                    name="duration"
                                    label="Durée (minutes)"
                                    keyboardType="number-pad"
                                    optional
                                    containerStyle={{ mb: 2 }}
                                    placeholder="Ex: 30"
                                />
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                {/* Questions */}
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h6">Questions</Typography>
                            <Chip
                                size="small"
                                label={`${watch('questions')?.length ?? 0} question(s)`}
                                color="primary"
                                variant="outlined"
                            />
                        </Stack>

                        <AddMoreWithReorder
                            control={control}
                            name="questions"
                            defaultObject={addQuestionDefault}
                            addMoreText="Ajouter une question"
                            render={props => <QuestionEditor {...props} />}
                        />
                    </CardContent>
                </Card>
            </Grid>

            {/* Right column - status */}
            <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ borderRadius: 2, position: 'sticky', top: 24 }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>
                            Statut
                        </Typography>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            value={status ?? QuestionnaireStatus.DRAFT}
                            onChange={e => {
                                setValue('status', e.target.value as QuestionnaireStatus, {
                                    shouldDirty: true,
                                });
                            }}
                            sx={{ mb: 2 }}>
                            {Object.values(QuestionnaireStatus).map((s) => {
                                const statusValue = s as QuestionnaireStatus;
                                return (
                                    <MenuItem key={s} value={s}>
                                        <Chip
                                            size="small"
                                            color={statusColor[statusValue]}
                                            label={QUESTIONNAIRE_STATUS_LABELS[statusValue]}
                                            sx={{ mr: 1 }}
                                        />
                                    </MenuItem>
                                );
                            })}
                        </TextField>

                        <Typography variant="body2" color="text.secondary">
                            <strong>Brouillon</strong> : en cours de création, non visible.{' '}
                            <br />
                            <strong>Actif</strong> : disponible pour être lié à une offre.{' '}
                            <br />
                            <strong>Archivé</strong> : masqué des listes par défaut.
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

interface QuestionnaireDetailProps {
    /** Explicit questionnaire id (overrides the :id route param). */
    questionnaireId?: string;
    /** Preset pro owner (e.g. company sub-page). */
    proId?: string | null;
    /** When true, the pro field is locked to the preset value. */
    lockPro?: boolean;
    /** Base path used for post-create navigation (default: '/questionnaires'). */
    basePath?: string;
}

const QuestionnaireDetail = ({
    questionnaireId,
    proId,
    lockPro = false,
    basePath = '/questionnaires',
}: QuestionnaireDetailProps = {}) => {
    const { methods, loading, isEdit, isLoaded, submit } = useSaveQuestionnaire({
        questionnaireId,
        proId,
        lockPro,
        basePath,
    });

    if (loading) {
        return <LoadingModal open message="Chargement du questionnaire..." />;
    }

    if (isEdit && !isLoaded) {
        return (
            <Box textAlign="center" py={8}>
                <Typography color="text.secondary">Questionnaire introuvable.</Typography>
            </Box>
        );
    }

    return (
        <FormProvider {...methods}>
            <Box sx={{ position: 'relative', pb: 0 }}>
                <QuestionnaireFormContent lockPro={lockPro} />
                <StickyBar>
                    <Button
                        variant="outlined"
                        onClick={async () => {
                            methods.setValue('status', QuestionnaireStatus.DRAFT, { shouldDirty: true });
                            await methods.handleSubmit(submit)();
                        }}>
                        {isEdit ? 'Enregistrer le brouillon' : 'Créer un brouillon'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={methods.handleSubmit(submit)}>
                        {isEdit ? 'Mettre à jour' : 'Créer le questionnaire'}
                    </Button>
                </StickyBar>
            </Box>
        </FormProvider>
    );
};

export default QuestionnaireDetail;
